import Pill from '@pelckmans/elementis/components/pill';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import MethodModuleStatus from '../../../components/tooltip/MethodModuleStatus';
import IdentityContext from '../../../contexts/IdentityContext';
import Role from '../../../core/security/role';
import { UserModule } from '../api/methodsApi';

const BASE = 'PAGES.MY_METHODS.MODULES.CARDS.STATUS';

function UserModuleStatus({
  access,
  module,
  newModule,
  phasedOutInPinnedSection,
}: {
  access: boolean;
  module: UserModule;
  newModule: boolean;
  phasedOutInPinnedSection: boolean;
}) {
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);
  const { id, publishingState, assignments, isDemo, incomplete } = module;

  const isAuthor = identity.author_modules.includes(module.id);
  const role = t(`ROLES.${isAuthor ? 'AUTHOR' : identity.highestRole.toUpperCase()}`);

  const ASSIGNMENT_BASE = `${BASE}.ASSIGNMENTS.${assignments === 1 ? 'SINGLE' : 'MULTIPLE'}`;

  return (
    <>
      {newModule && (
        <MethodModuleStatus
          id={`newmodule-${id}`}
          tooltipContent={t(`${BASE}.NEW.TOOLTIP.VALUE`)}
          pill={
            <Pill prominence="success" role="status" size="sm">
              {t(`${BASE}.NEW.LABEL`)}
            </Pill>
          }
        />
      )}
      {publishingState === 'preview' && (
        <MethodModuleStatus
          id={`preview-${id}`}
          tooltipContent={t(`${BASE}.PREVIEW.TOOLTIP.${access ? 'ACCESS' : 'NO_ACCESS'}`, { role })}
          pill={
            <Pill prominence="info" role="status" size="sm">
              {t(`${BASE}.PREVIEW.LABEL`)}
            </Pill>
          }
        />
      )}
      {publishingState === 'review' && (
        <MethodModuleStatus
          id={`review-${id}`}
          tooltipContent={t(`${BASE}.REVIEW.TOOLTIP.VALUE`, { role })}
          pill={
            <Pill prominence="info" role="status" size="sm">
              {t(`${BASE}.REVIEW.LABEL`)}
            </Pill>
          }
        />
      )}
      {phasedOutInPinnedSection && (
        <MethodModuleStatus
          id={`phasedout-${id}`}
          tooltipContent={t(`MODULES.STATUS.PHASED_OUT.TOOLTIP.VALUE`, { role })}
          pill={
            <Pill prominence="warning" role="status" size="sm">
              {t(`MODULES.STATUS.PHASED_OUT.LABEL`)}
            </Pill>
          }
        />
      )}
      {incomplete && (
        <MethodModuleStatus
          id={`incomplete-${id}`}
          tooltipContent={t(`${BASE}.INCOMPLETE.TOOLTIP.VALUE`, { role })}
          pill={
            <Pill prominence="warning" role="status" size="sm">
              {t(`${BASE}.INCOMPLETE.LABEL`)}
            </Pill>
          }
        />
      )}
      {isDemo && (
        <MethodModuleStatus
          id={`demo-${id}`}
          tooltipContent={t(`${BASE}.DEMO.TOOLTIP.VALUE`, { role })}
          pill={
            <Pill prominence="danger" role="status" size="sm">
              {t(`${BASE}.DEMO.LABEL`)}
            </Pill>
          }
        />
      )}
      {assignments >= 1 && identity.highestRole === Role.STUDENT && (
        <MethodModuleStatus
          id={`assignments-${id}`}
          tooltipContent={t(`${ASSIGNMENT_BASE}.TOOLTIP.VALUE`, { amount: assignments })}
          pill={
            <Pill role="status" size="sm">
              {t(`${ASSIGNMENT_BASE}.LABEL`, { amount: assignments })}
            </Pill>
          }
        />
      )}
    </>
  );
}

export default UserModuleStatus;
